import { useMemo, useRef, useState } from 'react';
import { ReadingStatus } from 'infrastructure/enums';
import { useFormik } from 'formik';
import { useAppSelector } from 'store';
import { useTranslation } from 'react-i18next';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import dataService from 'services/dataService';
import { API_READINGS_BULK_COMMENT_SUBMIT } from 'services/dataService/resources';
import common from 'constants/common';
import { showResult } from 'infrastructure/hooks/utils';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';
import { useRpmPatientDetails } from 'infrastructure/hooks/rpm';

import { getValidationSchemaByData } from './validation-schema';
import {
  aggregateData,
  appendBlurbsForStatus,
  checkNurseContactWithPatientValue,
  scrollToErrorElement,
} from './helpers';

import type { ReadingTemplateRoles } from 'infrastructure/enums';
import type {
  IRpmPatientFormActivity,
  TCompletedTemplate,
  TFormReadingStatus,
  TFormsReadingType,
  TTemplateRoleData,
} from './types';
import type { useTracker } from 'infrastructure/hooks/shared/useTracker';
import type { IRpmPatientActivity } from 'infrastructure/interfaces/i-rpm-patient-activity';

const initialValues = {
  [ReadingStatus.Risk]: {},
  [ReadingStatus.Normal]: {},
  [ReadingStatus.Critical]: null,
};

interface IProps {
  activity: IRpmPatientActivity | undefined;
  templateRole: ReadingTemplateRoles;
  patientGuid: string;
}

export const useTemplatesLogic = ({
  activity,
  templateRole,
  patientGuid,
}: IProps) => {
  const formWrapperRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof useTracker>>();
  const [completedList, setCompletedList] = useState<TCompletedTemplate[]>([]);
  const [reviewLoading, setReviewLoading] = useState<boolean>(false);
  const [currentReadingType, setCurrentReadingType] =
    useState<IRpmPatientFormActivity | null>(null);
  const [output, setOutput] = useState('');
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();
  const [startTime, endTime] = useAppSelector<[Date, Date]>(
    (state) => state.patient.dates,
  );
  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({
      patientGuid,
    });
  const { updateInteractionStatus } = useUpdateInteractionStatus();
  const {
    query: { patient },
  } = useRpmPatientDetails({ patientGuid });

  const aggData = useMemo(
    () => aggregateData(activity?.data ?? []),
    [activity?.data],
  );

  const formik = useFormik<TTemplateRoleData[typeof templateRole]>({
    initialValues: getValidationSchemaByData(
      aggData.sortedByStatusData,
      templateRole,
    ).cast(initialValues),
    onSubmit: () => {},
    validateOnChange: false,
  });

  const handleSaveCurrentForm = async (
    readingType: TFormsReadingType,
    status: TFormReadingStatus,
  ) => {
    const validationError = await formik.validateForm();
    const currentError = validationError?.[status]?.[readingType];

    if (currentError) {
      scrollToErrorElement(formWrapperRef.current);
      formik.setErrors({
        [status]: {
          [readingType]: currentError,
        },
      });
      return;
    }

    formik.setErrors({});

    let blurb = '';
    const templateData = formik.values[status][readingType];

    const isAllDataFieldsUndefined = Object.values(templateData ?? {}).every(
      (i) => i === undefined,
    );
    if (!templateData || isAllDataFieldsUndefined) {
      setCurrentReadingType(null);
      return;
    }

    blurb = appendBlurbsForStatus(
      templateRole,
      status,
      readingType,
      templateData,
    );

    setCompletedList((prev) => {
      let newPrev = structuredClone(prev);
      if (prev.length > 0) {
        const existingItem = prev.find(
          (i) => i.readingType === readingType && i.globalStatus === status,
        );

        if (existingItem) {
          newPrev = prev.map((item) => {
            if (
              item.readingType === readingType &&
              item.globalStatus === status
            ) {
              return {
                ...item,
                completed: true,
                blurb,
              };
            }
            return item;
          });
        } else {
          newPrev.push({
            readingType,
            globalStatus: status,
            completed: true,
            blurb,
          });
        }
      } else {
        newPrev.push({
          readingType,
          globalStatus: status,
          completed: true,
          blurb,
        });
      }

      const b = newPrev.map((item) => item.blurb).join('');
      setOutput(b);
      return newPrev;
    });
    setCurrentReadingType(null);
  };

  const handleSubmit = async () => {
    const confirmRes = await showAlert({
      title: t('confirm.bulkReviewReadings'),
      messageTitle: t('confirm.bulkReviewSave', {
        value: activity?.count ?? 0,
      }),
      successBtnLabel: t('controls.yes'),
      rejectBtnLabel: t('controls.no'),
    });
    if (!confirmRes) {
      return;
    }

    const postData = {
      patientGuid,
      comment: output,
      timerDuration: timerRef.current?.secondsRef.current,
      trackerGuid: timerRef.current?.trackerGuid,
      startTime,
      endTime,
    };
    timerRef.current?.stopTracker();
    setReviewLoading(true);

    // send bulk comment
    const { error } = await dataService.createOne(
      API_READINGS_BULK_COMMENT_SUBMIT,
      postData,
    );

    if (error) {
      showResult(error);
      timerRef.current?.resumeTracker();
    } else {
      showResult(null, common.submitSuccessTypes.bulkReviewed);
      // check if role==nurse and contact with patient question was selected as true
      const isPhoneCallCompleted = checkNurseContactWithPatientValue(
        templateRole,
        formik.values,
      );

      if (!patient?.profile.interactionStatus) {
        // if true - switch flag silently
        if (isPhoneCallCompleted) {
          await updateInteractionStatus({
            patientGuid,
            isInteracted: true,
          });
          // if false - show alert
        } else {
          const confirm = await showMonthlyInteractionAlert();
          if (confirm) {
            await updateInteractionStatus({
              patientGuid,
              isInteracted: true,
            });
          }
        }
      }
    }
    setReviewLoading(false);
  };

  return {
    aggData,
    formWrapperRef,
    timerRef,
    completedList,
    setCompletedList,
    currentReadingType,
    setCurrentReadingType,
    output,
    setOutput,
    handleSaveCurrentForm,
    formik,
    Alert,
    MonthlyInteractionAlert,
    handleSubmit,
    reviewLoading,
  };
};
